import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrentUser } from '@moussenaires/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { authApi } from '../api/auth.js';
import { ApiClientError, setAuthTokenGetter } from '../api/client.js';

const TOKEN_KEY = 'moussenaires.token';
const IMPERSONATION_KEY = 'moussenaires.impersonation';

interface AuthContextValue {
  user: CurrentUser | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  login: (token: string, impersonating?: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

let currentToken = readToken();
setAuthTokenGetter(() => currentToken);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(currentToken);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(() => {
    try {
      return localStorage.getItem(IMPERSONATION_KEY) === '1';
    } catch {
      return false;
    }
  });

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: authApi.me,
    enabled: token !== null,
    retry: false,
    staleTime: 60_000,
  });

  const login = useCallback(
    (nextToken: string, impersonating = false) => {
      currentToken = nextToken;
      setToken(nextToken);
      setIsImpersonating(impersonating);
      try {
        localStorage.setItem(TOKEN_KEY, nextToken);
        if (impersonating) localStorage.setItem(IMPERSONATION_KEY, '1');
        else localStorage.removeItem(IMPERSONATION_KEY);
      } catch {
        // Storage can be unavailable (private browsing); the session still works in-memory.
      }
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    currentToken = null;
    setToken(null);
    setIsImpersonating(false);
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      // ignore
    }
    queryClient.clear();
  }, [queryClient]);

  // A token that no longer resolves (expired, deactivated) means we're logged out.
  // This genuinely belongs in an effect rather than during render: `logout`
  // clears localStorage and the query cache, side effects on external systems
  // that render must stay free of — the lint rule's "derive state during
  // render" alternative doesn't apply to side effects, only to plain setState.
  const isExpiredToken =
    meQuery.isError && meQuery.error instanceof ApiClientError && meQuery.error.status === 401;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isExpiredToken && token) logout();
  }, [isExpiredToken, token, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data,
      isLoading: token !== null && meQuery.isLoading,
      isAuthenticated: Boolean(token && meQuery.data),
      isImpersonating,
      login,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, token, isImpersonating, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
